import { useState, useRef, useEffect } from "react";
import { Volume2, VolumeX, Volume1, Volume } from "lucide-react";
import { useAudioSettings } from "@/lib/audio-settings";

/**
 * Compact volume control — a speaker icon that toggles mute, plus a slider.
 * Persists to localStorage via `useAudioSettings`.
 */
export default function VolumeControl() {
  const [{ volume, muted }, setSettings] = useAudioSettings();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close slider on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const effective = muted ? 0 : volume;
  const Icon = muted || effective === 0 ? VolumeX
             : effective < 0.34         ? Volume
             : effective < 0.67         ? Volume1
             :                            Volume2;

  return (
    <div ref={wrapRef} className="relative" data-testid="volume-control">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={muted ? "Unmute announcements" : `Announcements ${Math.round(volume * 100)}%`}
        data-testid="volume-toggle"
        className="flex items-center gap-2 w-full px-3 py-2 rounded-md border border-border text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
      >
        <Icon className="w-4 h-4" />
        <span className="flex-1 text-left">{muted ? "Muted" : `Sound ${Math.round(volume * 100)}%`}</span>
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 w-full p-3 rounded-md border border-border bg-card shadow-xl z-50"
          data-testid="volume-popover"
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSettings({ muted: !muted })}
              data-testid="volume-mute"
              className={`p-1.5 rounded-md border transition-colors ${
                muted ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"
              }`}
              title={muted ? "Unmute" : "Mute"}
            >
              <Icon className="w-4 h-4" />
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(volume * 100)}
              onChange={(e) => setSettings({ volume: Number(e.target.value) / 100, muted: false })}
              data-testid="volume-slider"
              className="flex-1 accent-primary"
              disabled={muted}
            />
            <span className="text-xs font-mono w-8 text-right">
              {muted ? "—" : Math.round(volume * 100)}
            </span>
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            In-flight announcements
          </div>
        </div>
      )}
    </div>
  );
}
